import React, { useEffect, useMemo, useState } from 'react';
import * as PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon from '../icon/Icon';
import { soundManager } from '../../../../utils/scene/sound/SoundManager';
import { useTranslation } from '../../../../translations';

export default function Toggle({ startActive, tutorId, list, funcs, settings, mod, className }) {
  const [_current, setCurrent] = useState(startActive ?? 0);
  const { t } = useTranslation();

  // RTL-флаг из <html dir="...">
  const isRtl = typeof document !== 'undefined' && document?.documentElement?.getAttribute('dir') === 'rtl';

  useEffect(() => {
    setCurrent(startActive ?? 0);
  }, [startActive]);

  const length = useMemo(() => list?.length, [list]);

  // индекс с учётом зеркалирования в RTL
  const effectiveIndex = useMemo(() => {
    if (isRtl) return length - 1 - _current;
    return _current;
  }, [_current, isRtl, length]);

  // нормализация массива ширин до [числа] и суммы 100
  const normalizedWidths = useMemo(() => {
    const w = settings?.width;
    if (!Array.isArray(w) || w.length !== length) return null;
    const nums = w.map((v) => {
      const n = Number(v);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    });
    const sum = nums.reduce((s, n) => s + n, 0);
    if (sum <= 0) return null;
    if (sum === 100) return nums;
    // приводим к 100%
    return nums.map((n) => (n / sum) * 100);
  }, [settings, length]);

  const _styleMarker = useMemo(() => {
    // равные сегменты
    if (!normalizedWidths) {
      return {
        width: `${(1 / length) * 100}%`,
        transform: `translateX(${effectiveIndex * 100}%)`,
      };
    }

    // неравные сегменты
    const leftValue = normalizedWidths.reduce((sum, cur, id) => {
      if (id >= effectiveIndex || effectiveIndex === 0) return sum;
      return sum + cur;
    }, 0);

    return { left: `${leftValue}%`, width: `${normalizedWidths[effectiveIndex]}%` };
  }, [length, effectiveIndex, normalizedWidths]);

  const _styleItem = useMemo(() => {
    if (!normalizedWidths) return list.map(() => ({ width: `${100 / length}%` }));
    return list.map((_, id) => ({ width: `${normalizedWidths[id]}%` }));
  }, [list, length, normalizedWidths]);

  const onClick = (action, id) => {
    funcs[action]?.();
    setCurrent(id);
    soundManager.onPlay('slider');
  };

  return (
    <div
      className={classNames('toggle', className, {
        ['toggle_' + mod]: mod,
      })}
      {...(tutorId && { 'data-tutor-id': tutorId })}
    >
      <div className={'toggle__marker'} style={_styleMarker} />
      {list.map(({ text, icon, img, action }, id) => (
        <div
          key={`tgl-${id}`}
          className={classNames('toggle__item toggle__item_' + id, `${_current} ${id}`, {
            toggle__item_active: _current === id,
            toggle__item_char: action === 'character',
            toggle__item_room: action === 'room',
            toggle__item_bathroom: action === 'bathroom',
            toggle__item_beauties: action === 'beauties',
          })}
          style={_styleItem[id]}
          onClick={() => onClick(action, id)}
        >
          {icon && <Icon name={icon} className={'toggle__item-icon'} />}
          {img && <img src={img} className={'toggle__item-icon toggle__item-icon_img'} alt={''} />}
          {text && <div className={'toggle__item-text'}>{t(text)}</div>}
        </div>
      ))}
    </div>
  );
}

Toggle.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};
